import { Fragment, useEffect, useReducer, useState } from 'react';
import {
  draftActionMessage,
  draftButtonOf,
  draftCardActions,
  draftDropMessage,
  draftScheduleCutoff,
  draftScheduleMessage,
  draftWhen,
  type DraftCardButton,
  type DraftKind,
  type DraftMessage,
  type DraftPreviewCardData,
} from '../../../../lib/draft-preview';
import { useAgentName } from '../../../../lib/agent-name-context';
import { clearDraftTapBusy, draftTapBusyKey, draftTapBusyLeftMs, draftTapBusyShown, noteDraftTapBusy } from '../../../../lib/draft-tap-busy';
import type { SendDraftComments } from '../../../../lib/draft-review-store';
import type { DraftState } from '../../../../lib/draft-state';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { Button, Typography } from '../../ui';
import { DeliveredOutcome } from '../../tasks/DeliveredOutcome';
import styles from './DraftPreviewCard.module.css';

const MAX_TIMER_MS = 2_147_483_647;

interface DraftActionsProps {
  card: DraftPreviewCardData;
  state?: DraftState;
  spaceSlug: string;
  canSend: boolean;
  sendComments?: SendDraftComments;
  biPage?: string;
  revising?: boolean;
  publishTime?: boolean;
  onEditFirst: () => void;
  onSent: () => void;
}

const LABEL: Record<DraftCardButton, (kind: DraftKind) => string> = {
  schedule: () => 'draftPreview.schedule',
  deliverNow: (kind) => (kind === 'EMAIL' ? 'draftPreview.sendNow' : kind === 'SOCIAL_POST' ? 'draftPreview.postNow' : 'draftPreview.publishNow'),
  editFirst: () => 'draftPreview.editFirst',
  drop: () => 'draftPreview.drop',
  view: () => 'draftPreview.view',
  viewPost: () => 'blogDraft.card.view',
  edit: () => 'draftPreview.edit',
  publishChanges: () => 'draftPreview.publishChanges',
};

const BI: Record<DraftCardButton, (kind: DraftKind) => string> = {
  schedule: () => 'draft_preview_schedule',
  deliverNow: (kind) => (kind === 'EMAIL' ? 'draft_preview_send' : kind === 'SOCIAL_POST' ? 'draft_preview_post' : 'draft_preview_publish'),
  editFirst: () => 'draft_preview_edit_first',
  drop: () => 'draft_preview_drop',
  view: () => 'draft_preview_view_published',
  viewPost: () => 'draft_preview_view_published',
  edit: () => 'draft_preview_edit',
  publishChanges: () => 'draft_preview_publish',
};

export function DraftActions({
  card,
  state,
  spaceSlug,
  canSend,
  sendComments,
  biPage = 'chat',
  revising = false,
  publishTime = false,
  onEditFirst,
  onSent,
}: DraftActionsProps) {
  const { t, i18n } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const agentName = useAgentName();
  const email = card.kind === 'EMAIL';
  const copy = email ? 'emailReview' : card.kind === 'SOCIAL_POST' ? 'socialDraft' : 'blogDraft';
  const [newer, setNewer] = useState(false);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const busyKey = draftTapBusyKey(card);
  const tapBusy = draftTapBusyShown(busyKey, !canSend, Date.now());
  const cutoff = draftScheduleCutoff(state);
  const [cutoffTick, setCutoffTick] = useState(0);
  useEffect(() => {
    if (cutoff === null) return undefined;
    const wait = cutoff - Date.now();
    if (wait < 0) return undefined;
    const tick = setTimeout(() => setCutoffTick((n) => n + 1), Math.min(wait + 1, MAX_TIMER_MS));
    return () => clearTimeout(tick);
  }, [cutoff, cutoffTick]);
  useEffect(() => {
    if (!tapBusy || !canSend) return undefined;
    const expiry = setTimeout(rerender, draftTapBusyLeftMs(busyKey, Date.now()));
    return () => clearTimeout(expiry);
  }, [tapBusy, canSend, busyKey]);

  const now = Date.now();
  const actions = draftCardActions(card.kind, state, now, revising, publishTime);
  const scheduleAt = draftButtonOf(state, now);

  const tap = (message: string | DraftMessage) => {
    if (!sendComments || !spaceSlug) return;
    clearDraftTapBusy(busyKey);
    setNewer(false);
    const send: { text: string; draftAction?: DraftMessage['draftAction'] } = typeof message === 'string' ? { text: message } : message;
    const result = !canSend
      ? ({ status: 'busy' } as const)
      : send.draftAction
        ? sendComments(send.text, 'tap', send.draftAction)
        : sendComments(send.text, 'tap');
    if (result.status === 'sent') onSent();
    else if (result.status === 'newer') setNewer(true);
    else noteDraftTapBusy(busyKey, Date.now());
    rerender();
  };

  const press = (button: DraftCardButton) => {
    logPageClickBi({ page_name: biPage, button_name: BI[button](card.kind) }, { additionalFields: { kind: card.kind } });
    if (button === 'view' || button === 'viewPost') {
      if (state?.delivered?.url) window.open(state.delivered.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (button === 'editFirst' || button === 'edit') {
      onEditFirst();
      return;
    }
    if (button === 'drop') {
      tap(draftDropMessage(card, spaceSlug));
      return;
    }
    tap(
      button === 'schedule' && scheduleAt.kind === 'schedule'
        ? draftScheduleMessage(card, spaceSlug, scheduleAt.at)
        : draftActionMessage(card, spaceSlug),
    );
  };

  const held = (button: DraftCardButton) =>
    button !== 'view' && button !== 'viewPost' && (!sendComments || !spaceSlug || ((button === 'editFirst' || button === 'edit') && !canSend));

  if (actions.done) {
    return (
      <div className={styles.actions} data-testid="draftPreviewActions">
        <DeliveredOutcome
          kind={email ? 'email' : card.kind === 'SOCIAL_POST' ? 'social' : 'blog'}
          at={actions.done.at}
          url={actions.done.url}
          locale={i18n.language || undefined}
          onViewPost={(url) => {
            logPageClickBi({ page_name: biPage, button_name: BI.view(card.kind) }, { additionalFields: { kind: card.kind } });
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
        />
      </div>
    );
  }

  const statusLine = actions.status && (
    <Typography variant="p3" color="secondary" component="span" role="status" data-testid="draftPreviewStatus">
      {t(actions.status.key, actions.status.when ? { when: draftWhen(actions.status.when, i18n.language) } : {})}
    </Typography>
  );

  return (
    <div className={styles.actions} data-testid="draftPreviewActions">
      {actions.buttons.map((button, index) => (
        <Fragment key={button}>
          {index === actions.statusAt && statusLine}
          <Button
            size="small"
            variant={index === 0 && button !== 'view' && button !== 'edit' ? 'contained' : 'outlined'}
            disabled={held(button)}
            onClick={() => press(button)}
            data-testid={`draftPreview-${button}`}
          >
            {t(LABEL[button](card.kind))}
          </Button>
        </Fragment>
      ))}
      {actions.buttons.includes('schedule') && scheduleAt.kind === 'schedule' && (
        <Typography variant="p3" color="secondary" component="span" data-testid="draftPreviewPublishAt">
          {draftWhen(scheduleAt.at, i18n.language)}
        </Typography>
      )}
      {(actions.statusAt === undefined || actions.statusAt >= actions.buttons.length) && statusLine}
      {(tapBusy || newer) && (
        <Typography variant="p3" color="secondary" role="alert">
          {tapBusy ? t(`${copy}.refused.tapBusy`, { maestroAgentName: agentName.name }) : t(`${copy}.refused.newer`)}
        </Typography>
      )}
    </div>
  );
}
